
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BroData } from '@/lib/broCode';
import { estimateLiquidityValue } from '@/lib/liquidityProvider';
import { Loader2 } from 'lucide-react';

interface RerollDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentBrocode: string;
  isLoading: boolean;
  broData: BroData | null;
  onRerollWithXp: () => void;
  onRerollWithTokens: () => void;
  onRerollWithLiquidity: () => void;
}

const RerollDialog = ({
  isOpen,
  onClose,
  currentBrocode,
  isLoading,
  broData,
  onRerollWithXp,
  onRerollWithTokens,
  onRerollWithLiquidity
}: RerollDialogProps) => {
  const [estimatedBnb, setEstimatedBnb] = useState<string>('');
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    const getEstimate = async () => {
      if (isOpen) {
        setEstimating(true);
        try {
          const bnbAmount = await estimateLiquidityValue('5000');
          setEstimatedBnb(bnbAmount);
        } catch (error) {
          console.error('Error estimating BNB:', error);
          setEstimatedBnb('0.1'); // Fallback
        } finally {
          setEstimating(false);
        }
      }
    };
    
    getEstimate();
  }, [isOpen]);

  // Reset loading states when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setEstimating(false);
    }
  }, [isOpen]);

  const currentBroScore = broData?.bro_score || 0;
  const canRerollWithXp = currentBroScore >= 100;

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-navy-light border border-tan-dark text-tan max-w-md">
        <DialogHeader>
          <DialogTitle className="text-tan text-xl font-patrick">Reroll Your BroCode</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-tan/80">Current BroCode: <span className="font-mono text-tan">{currentBrocode}</span></p>
          <p className="text-sm text-tan/80">Current BroScore: <span className="text-tan">{currentBroScore}</span></p>
          
          <div className="space-y-3">
            <Button 
              onClick={onRerollWithXp}
              disabled={!canRerollWithXp || isLoading}
              className="w-full bg-tan text-navy-dark hover:bg-tan-dark"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reroll with 100 BroScore
            </Button>
            
            <Button 
              onClick={onRerollWithTokens}
              disabled={isLoading}
              className="w-full bg-navy-dark text-tan border border-tan hover:bg-navy"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reroll with 10,000 TOP1 Tokens
            </Button>

            <Button 
              onClick={onRerollWithLiquidity}
              disabled={isLoading || estimating}
              className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white hover:from-blue-700 hover:to-green-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reroll + Add Liquidity
              <div className="text-xs mt-1">
                {estimating ? (
                  'Calculating...'
                ) : (
                  `5,000 TOP1 + ${estimatedBnb ? `${parseFloat(estimatedBnb).toFixed(4)}` : '~0.1'} BNB`
                )}
              </div>
            </Button>
            
            {!canRerollWithXp && (
              <p className="text-xs text-red-400">Insufficient BroScore for XP reroll (need 100)</p>
            )}
          </div>
          
          <Button 
            onClick={handleClose}
            variant="outline"
            disabled={isLoading}
            className="w-full border-tan text-tan hover:bg-tan hover:text-navy-dark"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RerollDialog;
